<template>
    <div>
        <!-- Modal détail message -->
        <modal name="modal-message-detail" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-8 max-w-3xl overflow-y-auto max-h-[100vh]">
                <div class="flex items-center justify-between mb-4 pb-4 border-b">
                    <h2 class="text-xl font-bold flex items-center gap-2">
                        <i class="fas fa-envelope text-red-600"></i>
                        Détail du message
                    </h2>
                    <button @click="$modal.hide('modal-message-detail')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>

                <div v-if="selectedMessage" class="space-y-4 text-sm">
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <p class="text-gray-500">Destinataire</p>
                            <p class="font-medium">{{ selectedMessage.to }}</p>
                        </div>
                        <div>
                            <p class="text-gray-500">Statut</p>
                            <span
                                class="px-2 py-1 rounded text-xs font-semibold"
                                :class="selectedMessage.sent ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'"
                            >
                                {{ selectedMessage.sent ? 'Envoyé' : 'En attente' }}
                            </span>
                        </div>
                        <div>
                            <p class="text-gray-500">Programmé le</p>
                            <p>{{ formatDate(selectedMessage.scheduled_at) }}</p>
                        </div>
                        <div>
                            <template v-if="selectedMessage.type == 'email'">
                                <p class="text-gray-500">Sujet</p>
                                <p>{{ selectedMessage.subject || '-' }}</p>
                            </template>
                            <template v-if="selectedMessage.type == 'sms'">
                                <p class="text-gray-500">Numéro de téléphone</p>
                                <p>{{ selectedMessage.phone || '-' }}</p>
                            </template>
                        </div>
                    </div>

                    <div>
                        <p class="text-gray-500 mb-2">Contenu du message</p>
                        <div
                            class="border rounded-lg p-4 bg-gray-50 prose max-w-none"
                            v-html="selectedMessage.message.replace(/\n/g, '<br>')"
                        ></div>
                    </div>
                </div>
            </div>
        </modal>

        <app-template>
            <section class="space-y-8">

                <!-- Header -->
                <div>
                    <h2 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <i class="fas fa-comment-dots text-red-600"></i> Communications programmées
                    </h2>
                    <p class="text-sm text-gray-500 mt-1">
                        Emails et SMS en attente ou déjà envoyés.
                    </p>
                </div>

                <!-- Filtres -->
                <div class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                    <label class="text-sm font-medium text-gray-700">Rechercher par email</label>
                    <div class="relative mt-1">
                        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                        <input
                            v-model="filters.email"
                            type="text"
                            placeholder="ex: client@email.com"
                            class="pl-9 w-full rounded-lg border border-gray-300 text-sm py-2"
                        >
                    </div>
                </div>

                <!-- Tableau -->
                <div class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <table class="w-full text-sm text-left">
                        <thead class="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
                            <tr>
                                <th class="px-6 py-3">Type</th>
                                <th class="px-6 py-3">Email</th>
                                <th class="px-6 py-3">Tel</th>
                                <th class="px-6 py-3">Sujet</th>
                                <th class="px-6 py-3">Programmé le</th>
                                <th class="px-6 py-3">Statut</th>
                                <th class="px-6 py-3 text-right">Action</th>
                            </tr>
                        </thead>

                        <!-- Loader -->
                        <tbody v-if="loading" class="divide-y divide-gray-100">
                            <tr>
                                <td v-for="i in 5" :key="i" class="px-6 py-3">
                                    <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                                </td>
                            </tr>
                        </tbody>

                        <!-- Data -->
                        <tbody v-else class="divide-y divide-gray-100">
                            <tr
                                v-for="msg in messages.data"
                                :key="msg.id"
                                @click="openDetail(msg)"
                                class="hover:bg-red-50 cursor-pointer transition"
                            >
                                <td class="px-6 py-3">
                                    <i class="mr-1" :class="msg.type === 'email' ? 'fas fa-envelope' : 'fas fa-message'"></i> {{ msg.type === 'email' ? 'Email' : 'SMS' }}
                                </td>
                                <td class="px-6 py-3 font-medium">{{ msg.to || '-' }}</td>
                                <td class="px-6 py-3">{{ msg.phone || '-' }}</td>
                                <td class="px-6 py-3 truncate max-w-xs">{{ msg.subject || '-' }}</td>
                                <td class="px-6 py-3">{{ formatDate(msg.scheduled_at) }}</td>
                                <td class="px-6 py-3">
                                    <span
                                        class="px-2 py-1 rounded text-xs font-semibold"
                                        :class="msg.sent ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'"
                                    >
                                        {{ msg.sent ? 'Envoyé' : 'En attente' }}
                                    </span>
                                </td>
                                <td class="px-6 py-3 text-right">
                                    <button
                                        v-if="!msg.sent"
                                        @click.stop="deleteMessage(msg.id)"
                                        class="text-red-600 hover:text-red-800"
                                    >
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>

                            <tr v-if="!messages.data.length">
                                <td colspan="7" class="px-6 py-4 text-center text-gray-400 italic">
                                    Aucun message programmé
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="flex justify-between items-center text-sm text-gray-600">
                    <p>
                        {{ messages.from || 0 }} – {{ messages.to || 0 }} / {{ messages.total || 0 }}
                    </p>

                    <div class="flex gap-1">
                        <button
                            :disabled="!messages.prev_page_url"
                            @click="fetchMessages(messages.current_page - 1)"
                            class="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Précédent
                        </button>

                        <span
                            v-for="page in pages"
                            :key="page"
                            @click="fetchMessages(page)"
                            class="px-3 py-1 border rounded cursor-pointer"
                            :class="page === messages.current_page ? 'font-bold text-red-600 border-red-400' : ''"
                        >
                            {{ page }}
                        </span>

                        <button
                            :disabled="!messages.next_page_url"
                            @click="fetchMessages(messages.current_page + 1)"
                            class="px-3 py-1 border rounded disabled:opacity-50"
                        >
                            Suivant
                        </button>
                    </div>
                </div>

            </section>
        </app-template>
    </div>
</template>

<script>
export default {
    name: 'WaitingMessageList',
    data() {
        return {
            messages: { data: [] },
            filters: {
                email: ''
            },
            loading: false,
            pages: [],
            searchTimeout: null,
            selectedMessage: null
        }
    },
    mounted() {
        this.fetchMessages()
    },
    watch: {
        'filters.email'() {
            this.debouncedFetch()
        }
    },
    methods: {
        async fetchMessages(page = 1) {
            this.loading = true
            try {
                const res = await this.$request.get('/waiting-messages', {
                    params: {
                        page,
                        email: this.filters.email || ''
                    }
                })
                this.messages = res
                this.generatePagination()
            } catch (e) {
                this.$toast.error('Erreur de chargement')
            } finally {
                setTimeout(() => { this.loading = false }, 200)
            }
        },
        debouncedFetch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.fetchMessages()
            }, 400)
        },
        generatePagination() {
            if (!this.messages.last_page) return
            const pages = []
            for (
                let i = Math.max(1, this.messages.current_page - 2);
                i <= Math.min(this.messages.last_page, this.messages.current_page + 2);
                i++
            ) {
                pages.push(i)
            }
            this.pages = pages
        },
        openDetail(msg) {
            this.selectedMessage = msg
            this.$modal.show('modal-message-detail')
        },
        async deleteMessage(id) {
            if (!confirm('Supprimer ce message ?')) return
            await this.$request.post('/waiting-messages/delete', { id })
            this.$toast.success('Message supprimé')
            this.fetchMessages(this.messages.current_page)
        },
        formatDate(date) {
            return date ? new Date(date).toLocaleString('fr-FR') : '-'
        }
    }
}
</script>
